import { useIsTouchDevice, useStableImperativeCallback } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { EditorState } from "prosemirror-state";
import { DirectEditorProps, EditorView } from "prosemirror-view";
import {
	RefObject,
	useEffect,
	useMemo,
	useRef
} from "react";
import { ProsemirrorViewHandle } from "../../ProsemirrorView/ProsemirrorView";

export type SelectionAnchor = {
	x: number;
	top: number;
	bottom: number;
	direction: SelectionDirection;
};

export type SelectionDirection = "forward" | "backward";

export type useSelectionAnchorParams = {
	viewRef: RefObject<ProsemirrorViewHandle | null>;
	editorState: EditorState;
	onSelectionAnchorChanged?: (anchor: SelectionAnchor | null) => void;
};

export type UseSelectionAchorResult = {
	handleDOMEvents: DirectEditorProps["handleDOMEvents"] | undefined;
};

export function useSelectionAnchor(params: useSelectionAnchorParams): UseSelectionAchorResult {
	const {
		viewRef,
		editorState,
		onSelectionAnchorChanged
	} = params;

	const isTouchDevice: boolean = useIsTouchDevice();
	const isEnabled: boolean = !isTouchDevice && isNotNil(onSelectionAnchorChanged);
	const isPointerActiveRef: RefObject<boolean> = useRef<boolean>(false);

	const emitSelectionMenu = useStableImperativeCallback(
		(): void => {
			const view: EditorView | nil = viewRef.current
				?.getProsemirrorViewObject();

			if (!view || !onSelectionAnchorChanged) {
				return;
			}

			const {
				selection
			} = view.state;

			if (selection.empty) {
				onSelectionAnchorChanged(null);
				return;
			}

			const isForward: boolean = selection.head >= selection.anchor;

			const {
				left,
				top,
				right,
				bottom
			} = view.coordsAtPos(selection.head);

			onSelectionAnchorChanged({
				x: isForward
					? right
					: left,
				top: top,
				bottom: bottom,
				direction: isForward
					? "forward"
					: "backward"
			});
		}
	);

	const hideSelectionMenu = useStableImperativeCallback(
		(): void => {
			onSelectionAnchorChanged?.(null);
		}
	);

	useEffect(
		(): void => {
			if (!isEnabled) {
				return;
			}

			if (isPointerActiveRef.current) {
				return;
			}

			emitSelectionMenu();
		},
		[
			editorState,
			isEnabled,
			emitSelectionMenu
		]
	);

	useEffect(
		(): void => {
			if (!isEnabled) {
				hideSelectionMenu();
			}
		},
		[isEnabled, hideSelectionMenu]
	);

	const handleDOMEvents = useMemo(
		(): DirectEditorProps["handleDOMEvents"] | undefined => {
			if (!isEnabled) {
				return undefined;
			}

			return {
				mousedown: (): boolean => {
					isPointerActiveRef.current = true;

					hideSelectionMenu();

					document.addEventListener(
						"mouseup",
						(): void => {
							isPointerActiveRef.current = false;
							emitSelectionMenu();
						},
						{ once: true }
					);

					return false;
				}
			};
		},
		[
			isEnabled,
			emitSelectionMenu,
			hideSelectionMenu
		]
	);

	return {
		handleDOMEvents
	};
}
